import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import { useQuery } from '@tanstack/react-query';
import FigurService from '../services/figureService';

export interface TagsFilterProps {
	/** Selected tag names array. Empty array means no filter */
	value: string[];
	/** Called when selection changes */
	onChange: (tags: string[]) => void;
	/** Customer ID for fetching tags */
	customerId: number;
	/** Optional className for outer container */
	className?: string;
}

/**
 * Multi-select dropdown for filtering figures by tags.
 * Supports autocomplete when typing and displays selected tags as chips.
 */
const TagsFilter: React.FC<TagsFilterProps> = ({ value, onChange, customerId, className }) => {
	const [inputValue, setInputValue] = useState('');

	// Fetch all available tags
	const { data: availableTags = [], isLoading } = useQuery({
		queryKey: ['allTags', customerId],
		queryFn: () => FigurService.getAllTags(customerId),
		staleTime: 5 * 60 * 1000, // 5 minutes
		refetchOnWindowFocus: false,
	});

	return (
		<Box className={className} sx={{ display: 'flex', alignItems: 'center' }}>
			<Autocomplete
				multiple
				options={availableTags}
				value={value}
				onChange={(_event, newValue) => {
					onChange(newValue);
				}}
				inputValue={inputValue}
				onInputChange={(_event, newInputValue) => {
					setInputValue(newInputValue);
				}}
				loading={isLoading}
				size="small"
				sx={{ minWidth: 220 }}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Tags"
						variant="outlined"
						InputLabelProps={{ shrink: true }}
						sx={{
							'& .MuiOutlinedInput-notchedOutline': {
								borderWidth: '1px',
							},
						}}
					/>
				)}
				renderTags={(selected, getTagProps) =>
					selected.map((option, index) => (
						<Chip
							{...getTagProps({ index })}
							key={option}
							label={option}
							size="small"
						/>
					))
				}
				filterOptions={(options, { inputValue: filterValue }) => {
					// Case-insensitive filtering
					const lowerFilter = filterValue.toLowerCase();
					return options.filter((option) =>
						option.toLowerCase().includes(lowerFilter)
					);
				}}
				noOptionsText="Ingen tags funnet"
				loadingText="Laster tags..."
			/>
		</Box>
	);
};

export default TagsFilter;

